import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, Search, Check, Loader2, X, ArrowRight, School, RefreshCw } from "lucide-react";

/**
 * SchoolVerifyModal — the single place users unlock school verification
 * across the app. Used by the AI Try-On paywall, cart checkout, dress lock,
 * anywhere else a school-verified account is required.
 *
 * Steps:
 *   1. Pick school (searchable list of the 258 WI schools)
 *   2. Enter school email → send magic link
 *   3. "Check your inbox" — with resend + change-email + "my school isn't listed"
 */
export function SchoolVerifyModal({ open, onClose, reason = "unlock" }) {
  const { refresh: refreshMe } = useAuth();
  const [step, setStep] = useState(0);           // 0 pick, 1 email, 2 sent, 3 add-request
  const [q, setQ] = useState("");
  const [schools, setSchools] = useState([]);
  const [school, setSchool] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [addName, setAddName] = useState("");
  const [addCity, setAddCity] = useState("");
  const [addNote, setAddNote] = useState("");
  const timeout = useRef();

  // debounced search
  useEffect(() => {
    if (!open) return;
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      api.schools({ q: q || undefined, limit: 24 })
        .then(({ data }) => setSchools((data.schools || data || []).slice(0, 24)))
        .catch(() => setSchools([]));
    }, 250);
    return () => clearTimeout(timeout.current);
  }, [q, open]);

  const reset = () => {
    setStep(0); setQ(""); setSchool(null); setEmail("");
    setAddName(""); setAddCity(""); setAddNote(""); setBusy(false);
  };
  const close = () => { onClose(); setTimeout(reset, 300); };

  const sendLink = async () => {
    if (!school || !email.trim()) { toast("Pick your school and enter your email first."); return; }
    setBusy(true);
    try {
      await api.requestSchoolVerify({ school_id: school.id, email: email.trim() });
      setStep(2);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.resendSchoolVerify({ email: email.trim() });
      toast.success("Resent — check your inbox (and spam!)");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const submitAdd = async () => {
    if (!addName.trim()) { toast("What's your school called?"); return; }
    setBusy(true);
    try {
      await api.requestAddSchool({ school_name: addName.trim(), city: addCity.trim() || null, note: addNote.trim() || null, email: email.trim() || null });
      toast.success("Got it! Heather will add your school within 24 hours.");
      close();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  // On mount, if backend already knows the user's school (persisted via
  // /auth/school/request-verify) we can skip step 0.
  useEffect(() => {
    if (open && !school) {
      refreshMe?.().then(() => {}).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const headline = {
    tryon: "Unlock your free AI try-on.",
    checkout: "One quick step before checkout.",
    unlock: "Verify your school to unlock this.",
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center p-3 sm:p-6"
          onClick={close}
          data-testid="school-verify-modal"
        >
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-md" />
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-ivory rounded-sm w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl"
          >
            <button onClick={close} data-testid="school-verify-close"
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-ink/70 text-ivory flex items-center justify-center hover:bg-ink z-20">
              <X className="w-4 h-4" />
            </button>

            <div className="p-6 sm:p-8">
              <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-2 flex items-center gap-2">
                <School className="w-3 h-3" /> SCHOOL VERIFICATION · 15 SECONDS
              </p>
              <h3 className="font-display text-3xl sm:text-4xl leading-tight text-ink mb-2">{headline[reason] || headline.unlock}</h3>
              <p className="font-body text-sm text-ink/60 mb-6 leading-relaxed">
                Heather's is students-only. Pick your school, tell us your school email, and we'll send a magic link — no password, no fuss.
              </p>

              {/* STEP 0 — pick school */}
              {step === 0 && (
                <div className="space-y-4" data-testid="school-step-pick">
                  <div className="relative">
                    <Search className="absolute left-3 top-3.5 w-4 h-4 text-ink/40" />
                    <input
                      data-testid="school-search"
                      value={q} onChange={(e) => setQ(e.target.value)}
                      placeholder="Search Wisconsin high schools…"
                      className="w-full border border-cream rounded-sm pl-10 pr-3 py-3 font-body text-sm outline-none focus:border-rosemetal"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-cream rounded-sm divide-y divide-cream">
                    {schools.length === 0 && (
                      <div className="px-4 py-6 text-center font-body text-xs text-ink/50">No schools match — try a shorter search.</div>
                    )}
                    {schools.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`school-pick-${s.id}`}
                        onClick={() => { setSchool(s); setStep(1); }}
                        className="w-full text-left px-4 py-3 hover:bg-blush/40 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-body text-sm text-ink">{s.name}</div>
                          <div className="font-label text-[9px] tracking-widest text-ink/50">{s.city} · {s.county || "Wisconsin"}</div>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-rosemetal" />
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setStep(3)}
                    data-testid="school-request-add-open"
                    className="w-full text-center font-label text-[10px] tracking-widest text-ink/60 hover:text-rosemetal pt-1"
                  >
                    MY SCHOOL ISN'T LISTED →
                  </button>
                </div>
              )}

              {/* STEP 1 — enter school email */}
              {step === 1 && school && (
                <div className="space-y-4" data-testid="school-step-email">
                  <div className="bg-cream/50 rounded-sm p-4 flex items-center gap-3">
                    <School className="w-4 h-4 text-rosemetal shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-body text-sm text-ink truncate">{school.name}</div>
                      <div className="font-label text-[9px] tracking-widest text-ink/50">{school.city}</div>
                    </div>
                    <button data-testid="school-change" onClick={() => setStep(0)} className="font-label text-[9px] text-rosemetal">CHANGE</button>
                  </div>
                  <label className="block">
                    <span className="font-label text-[9px] tracking-widest text-ink/60 mb-1.5 block">YOUR SCHOOL EMAIL</span>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3.5 w-4 h-4 text-ink/40" />
                      <input
                        data-testid="school-email-input"
                        type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="jane@yourschool.k12.wi.us"
                        className="w-full border border-cream rounded-sm pl-10 pr-3 py-3 font-body text-sm outline-none focus:border-rosemetal"
                      />
                    </div>
                    <p className="font-label text-[9px] tracking-widest text-ink/40 mt-2">USE YOUR REAL SCHOOL INBOX — WE'LL SEND A ONE-TIME LINK</p>
                  </label>
                  <button
                    onClick={sendLink}
                    disabled={busy}
                    data-testid="school-send-link"
                    className="w-full bg-ink text-cream py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                  >
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4" /> Send Magic Link</>}
                  </button>
                </div>
              )}

              {/* STEP 2 — check inbox */}
              {step === 2 && (
                <div className="text-center space-y-4" data-testid="school-step-sent">
                  <div className="w-16 h-16 mx-auto rounded-full bg-champagne/30 border border-champagne flex items-center justify-center">
                    <Mail className="w-7 h-7 text-rosemetal" strokeWidth={2} />
                  </div>
                  <h4 className="font-display text-3xl">Check your inbox.</h4>
                  <p className="font-body text-sm text-ink/60 leading-relaxed">
                    We just sent a magic link to <b className="text-ink">{email}</b>. Tap it to confirm and you're in.
                  </p>
                  <div className="bg-cream/50 border border-cream rounded-sm p-4 text-left text-xs font-body text-ink/70 space-y-1.5">
                    <p>· Link expires in 15 minutes</p>
                    <p>· Check spam if you don't see it</p>
                    <p>· Wrong email? Tap "resend" below and update it</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={resend} disabled={busy}
                      data-testid="school-resend"
                      className="flex-1 border border-ink text-ink py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} RESEND
                    </button>
                    <button
                      onClick={() => setStep(1)}
                      className="flex-1 border border-cream text-ink py-3 font-label text-[10px] tracking-widest rounded-sm hover:border-ink transition-colors"
                    >
                      CHANGE EMAIL
                    </button>
                  </div>
                  <p className="font-label text-[9px] tracking-widest text-ink/40">MAX 3 RESENDS / HOUR</p>
                </div>
              )}

              {/* STEP 3 — request add school */}
              {step === 3 && (
                <div className="space-y-3" data-testid="school-step-request">
                  <p className="font-body text-sm text-ink/70">
                    Tell us about your school and we'll add it within 24 hours. Heather will text you the moment it's live.
                  </p>
                  <input
                    data-testid="add-school-name"
                    value={addName} onChange={(e) => setAddName(e.target.value)}
                    placeholder="School name (e.g. Cedar Grove High)"
                    className="w-full border border-cream rounded-sm px-3 py-3 font-body text-sm outline-none focus:border-rosemetal"
                  />
                  <input
                    data-testid="add-school-city"
                    value={addCity} onChange={(e) => setAddCity(e.target.value)}
                    placeholder="City (optional)"
                    className="w-full border border-cream rounded-sm px-3 py-3 font-body text-sm outline-none focus:border-rosemetal"
                  />
                  <textarea
                    data-testid="add-school-note"
                    rows={2} value={addNote} onChange={(e) => setAddNote(e.target.value)}
                    placeholder="Anything else? (school email domain, etc.)"
                    className="w-full border border-cream rounded-sm px-3 py-2.5 font-body text-sm outline-none focus:border-rosemetal"
                  />
                  <div className="flex gap-3">
                    <button onClick={() => setStep(0)} className="flex-1 border border-cream text-ink py-3 font-label text-[10px] tracking-widest rounded-sm hover:border-ink">BACK</button>
                    <button
                      onClick={submitAdd} disabled={busy}
                      data-testid="add-school-submit"
                      className="flex-1 bg-ink text-cream py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-rosemetal transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} SEND REQUEST
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SchoolVerifyModal;
